/* CapturistaAcPage — vista exclusiva del rol `capturista_ac` (Luisa).
   - Solo lectura: reportes de falla + cambios de riego (todas las zonas).
   - Filtros por zona y rango de fechas.
   - Descarga Excel con ambas pestañas.
   - Solicitar pipa (cross-zona, vía SubModalSolicitarPipa). */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import { SubModalSolicitarPipa } from "../components/ModalParque";
import "../styles/modal-parque.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ZONAS = Array.from({ length: 20 }, (_, i) => `Z${i + 1}`);

function fmt(iso) {
  if (!iso) return "—";
  const s = iso.replace("T", " ");
  return s.length >= 16 ? s.slice(0, 16) : s;
}

function PillEstado({ valor }) {
  const map = {
    FUNCIONANDO:      { bg: "#DCFCE7", fg: "#15803D", label: "FUNCIONANDO" },
    FUERA_DE_SERVICIO:{ bg: "#FEE2E2", fg: "#B91C1C", label: "FUERA DE SERVICIO" },
    BIEN:             { bg: "#DCFCE7", fg: "#15803D", label: "BIEN" },
    MAL:              { bg: "#FEF3C7", fg: "#B45309", label: "MAL" },
    NO_FUNCIONA:      { bg: "#FEE2E2", fg: "#B91C1C", label: "NO FUNCIONA" },
  };
  const cfg = map[valor] || { bg: "#E2E8F0", fg: "#475569", label: valor || "—" };
  return (
    <span style={{
      background: cfg.bg, color: cfg.fg, padding: "2px 8px",
      borderRadius: 999, fontWeight: 700, fontSize: "0.7rem", whiteSpace: "nowrap",
    }}>{cfg.label}</span>
  );
}

export default function CapturistaAcPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("reportes"); // "reportes" | "cambios"
  const [zona, setZona] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const [reportes, setReportes] = useState([]);
  const [cambios, setCambios] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openSolicitarSearch, setOpenSolicitarSearch] = useState(false);
  const [parqueSolicitar, setParqueSolicitar] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      await Promise.resolve();
      if (!active) return;
      setLoading(true);
      try {
        const params = {};
        if (zona) params.zona = zona;
        if (desde) params.desde = desde;
        if (hasta) params.hasta = hasta;
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/capturista-ac/reportes-falla`, { params }),
          axios.get(`${API}/capturista-ac/cambios-riego`,   { params }),
        ]);
        if (active) {
          setReportes(r1.data.items || []);
          setCambios(r2.data.items || []);
        }
      } catch (e) {
        toast.error("No se pudo cargar la información: " + (e.response?.data?.detail || e.message));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [zona, desde, hasta]);

  const descargarExcel = async () => {
    try {
      const params = {};
      if (zona) params.zona = zona;
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const res = await axios.get(`${API}/capturista-ac/exportar-excel`, {
        params, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const yyyymmdd = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `DMU614_Reportes_AC_${yyyymmdd}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (e) {
      toast.error("Error al descargar Excel");
    }
  };

  const dataActiva = tab === "reportes" ? reportes : cambios;
  const totalTab = dataActiva.length;

  return (
    <div className="hub-bg min-h-screen" data-testid="page-capturista-ac">
      {/* Header */}
      <header className="topnav">
        <div className="brand">
          <div className="logo" style={{ background: "#1E40AF" }}>
            <i className="fa-solid fa-file-lines"/>
          </div>
          <div>
            <div className="brand-title">Áreas Contratadas — Reportes</div>
            <div className="brand-sub">Hola, {user?.nombre || user?.usuario} · solo lectura + descarga</div>
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-3">
          <button
            onClick={descargarExcel}
            className="btn-primary"
            data-testid="btn-descargar-excel"
            style={{ background: "#16A34A", whiteSpace: "nowrap" }}
            title="Descargar Excel con ambas pestañas"
          >
            <i className="fa-solid fa-file-excel mr-1.5"/> Descargar Excel
          </button>
          <button
            onClick={() => setOpenSolicitarSearch(true)}
            className="btn-primary"
            data-testid="btn-solicitar-pipa"
            style={{ background: "#0EA5E9", whiteSpace: "nowrap" }}
            title="Solicitar pipa para cualquier parque"
          >
            <i className="fa-solid fa-truck-droplet mr-1.5"/> Solicitar pipa
          </button>
          <button onClick={() => navigate("/hub")} className="btn-primary" data-testid="btn-back-hub"
            style={{ background: "#334155", whiteSpace: "nowrap" }}>
            <i className="fa-solid fa-house mr-1"/>Menú
          </button>
          <button onClick={logout} data-testid="btn-logout" className="btn-primary"
            style={{ background: "#334155", whiteSpace: "nowrap" }}>
            <i className="fa-solid fa-right-from-bracket mr-1"/>Salir
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        {/* Filtros */}
        <section className="card-base p-3 flex flex-wrap items-center gap-3" data-testid="filtros">
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold uppercase text-slate-500">Zona</label>
            <select
              value={zona}
              onChange={(e) => setZona(e.target.value)}
              data-testid="filtro-zona"
              className="border border-slate-300 rounded-md px-2 py-1.5 text-sm"
              style={{ minWidth: 120 }}
            >
              <option value="">Todas</option>
              {ZONAS.map(z => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold uppercase text-slate-500">Desde</label>
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              data-testid="filtro-desde"
              className="border border-slate-300 rounded-md px-2 py-1.5 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold uppercase text-slate-500">Hasta</label>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              data-testid="filtro-hasta"
              className="border border-slate-300 rounded-md px-2 py-1.5 text-sm"
            />
          </div>
          {(zona || desde || hasta) && (
            <button
              type="button"
              onClick={() => { setZona(""); setDesde(""); setHasta(""); }}
              data-testid="btn-limpiar-filtros"
              className="text-xs text-blue-700 hover:underline"
            >
              <i className="fa-solid fa-xmark mr-1"/>Limpiar filtros
            </button>
          )}
          {loading && <span className="text-xs text-slate-500 ml-auto"><i className="fa-solid fa-spinner fa-spin mr-1"/>Cargando…</span>}
        </section>

        {/* Tabs */}
        <section className="card-base p-0 overflow-hidden">
          <div className="flex border-b border-slate-200" role="tablist">
            <button
              role="tab"
              aria-selected={tab === "reportes"}
              onClick={() => setTab("reportes")}
              data-testid="tab-reportes-falla"
              className="flex-1 py-3 px-4 text-sm font-bold transition-colors"
              style={{
                background: tab === "reportes" ? "#FEE2E2" : "transparent",
                color: tab === "reportes" ? "#B91C1C" : "#475569",
                borderBottom: tab === "reportes" ? "3px solid #B91C1C" : "3px solid transparent",
              }}
            >
              <i className="fa-solid fa-triangle-exclamation mr-1.5"/>
              Reportes de falla
              <span className="ml-2 pill-tiny" style={{ background: tab === "reportes" ? "#B91C1C" : "#94A3B8", color: "white" }}>
                {reportes.length}
              </span>
            </button>
            <button
              role="tab"
              aria-selected={tab === "cambios"}
              onClick={() => setTab("cambios")}
              data-testid="tab-cambios-riego"
              className="flex-1 py-3 px-4 text-sm font-bold transition-colors"
              style={{
                background: tab === "cambios" ? "#DBEAFE" : "transparent",
                color: tab === "cambios" ? "#1D4ED8" : "#475569",
                borderBottom: tab === "cambios" ? "3px solid #1D4ED8" : "3px solid transparent",
              }}
            >
              <i className="fa-solid fa-arrows-rotate mr-1.5"/>
              Cambios de riego
              <span className="ml-2 pill-tiny" style={{ background: tab === "cambios" ? "#1D4ED8" : "#94A3B8", color: "white" }}>
                {cambios.length}
              </span>
            </button>
          </div>

          {/* Contenido por tab */}
          <div className="overflow-x-auto" data-testid={tab === "reportes" ? "tabla-reportes-falla" : "tabla-cambios-riego"}>
            {totalTab === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">
                <i className="fa-solid fa-inbox text-3xl mb-2 block text-slate-300"/>
                No hay registros para los filtros actuales.
              </div>
            ) : tab === "reportes" ? (
              <TablaReportes filas={reportes} />
            ) : (
              <TablaCambios filas={cambios} />
            )}
          </div>
        </section>

        {/* Sección: Solicitar pipa por parque (lista completa) */}
        <SolicitarPipaSection
          onSeleccionar={(p) => setParqueSolicitar(p)}
        />

        <div className="text-xs text-slate-500 px-1">
          <i className="fa-solid fa-circle-info mr-1"/>
          Vista de solo lectura. Todas las zonas. Para descargar el Excel con la
          información completa usa el botón <strong>Descargar Excel</strong> arriba.
        </div>
      </main>

      {/* Modal: buscar parque para solicitar pipa */}
      {openSolicitarSearch && (
        <BuscarParqueModal
          onClose={() => setOpenSolicitarSearch(false)}
          onSeleccionar={(p) => { setOpenSolicitarSearch(false); setParqueSolicitar(p); }}
        />
      )}

      {parqueSolicitar && (
        <SubModalSolicitarPipa
          parque={parqueSolicitar}
          onClose={() => setParqueSolicitar(null)}
        />
      )}
    </div>
  );
}

/* ── Tabla: reportes de falla ─────────────────────────────── */
function TablaReportes({ filas }) {
  return (
    <table className="min-w-full text-sm">
      <thead className="bg-slate-50">
        <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
          <th className="p-3">Fecha</th>
          <th className="p-3">Parque</th>
          <th className="p-3">ID</th>
          <th className="p-3">Zona</th>
          <th className="p-3">Supervisor</th>
          <th className="p-3">Estado</th>
          <th className="p-3">Requiere acción</th>
          <th className="p-3">Tipo agua</th>
          <th className="p-3">Observaciones</th>
          <th className="p-3">Foto</th>
        </tr>
      </thead>
      <tbody>
        {filas.map((r, i) => (
          <tr key={i} className="border-t border-slate-100" data-testid={`fila-reporte-${i}`}>
            <td className="p-3 text-xs mono whitespace-nowrap">{fmt(r.fecha)}</td>
            <td className="p-3 font-medium">{r.parque}</td>
            <td className="p-3 text-xs mono">{r.id_oficial}</td>
            <td className="p-3"><span className="pill-tiny pill-zone">{r.zona}</span></td>
            <td className="p-3 text-sm">{r.supervisor}</td>
            <td className="p-3"><PillEstado valor={r.estado_riego}/></td>
            <td className="p-3 text-xs">{r.requiere_accion}</td>
            <td className="p-3 text-xs">{r.tipo_agua}</td>
            <td className="p-3 text-xs max-w-xs truncate" title={r.observaciones}>{r.observaciones || "—"}</td>
            <td className="p-3 text-xs">
              {r.foto_url ? (
                <a href={r.foto_url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                  <i className="fa-solid fa-image mr-1"/>Ver
                </a>
              ) : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── Tabla: cambios de riego ──────────────────────────────── */
function TablaCambios({ filas }) {
  return (
    <table className="min-w-full text-sm">
      <thead className="bg-slate-50">
        <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
          <th className="p-3">Fecha</th>
          <th className="p-3">Parque</th>
          <th className="p-3">ID</th>
          <th className="p-3">Zona</th>
          <th className="p-3">Supervisor</th>
          <th className="p-3">Estado anterior</th>
          <th className="p-3">Estado nuevo</th>
          <th className="p-3">Observaciones</th>
        </tr>
      </thead>
      <tbody>
        {filas.map((c, i) => (
          <tr key={i} className="border-t border-slate-100" data-testid={`fila-cambio-${i}`}>
            <td className="p-3 text-xs mono whitespace-nowrap">{fmt(c.fecha)}</td>
            <td className="p-3 font-medium">{c.parque}</td>
            <td className="p-3 text-xs mono">{c.id_oficial}</td>
            <td className="p-3"><span className="pill-tiny pill-zone">{c.zona}</span></td>
            <td className="p-3 text-sm">{c.supervisor}</td>
            <td className="p-3"><PillEstado valor={c.estado_anterior}/></td>
            <td className="p-3"><PillEstado valor={c.estado_nuevo}/></td>
            <td className="p-3 text-xs">{c.observaciones || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── Sección: Solicitar pipa con lista completa de parques ──
   Carga TODOS los parques (20 zonas en paralelo, evita el cap de 500).
   Filtros locales por zona + nombre/ID. Click → SubModalSolicitarPipa. */
function SolicitarPipaSection({ onSeleccionar }) {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zonaSel, setZonaSel] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      await Promise.resolve();
      if (!active) return;
      setLoading(true);
      try {
        // Carga en paralelo por zona para evitar el cap de 500 docs del endpoint.
        const reqs = ZONAS.map(z =>
          axios.get(`${API}/espacios`, { params: { zona_id: z, limit: 200 } })
            .then(r => r.data || [])
            .catch(() => [])
        );
        const lotes = await Promise.all(reqs);
        if (!active) return;
        const planos = lotes.flat();
        planos.sort((a, b) => {
          const za = (a.zona_id || "").localeCompare(b.zona_id || "", "es", { numeric: true });
          if (za !== 0) return za;
          return (a.id_oficial || "").localeCompare(b.id_oficial || "", "es", { numeric: true });
        });
        setTodos(planos);
      } catch (e) {
        toast.error("No se pudieron cargar los parques: " + (e.response?.data?.detail || e.message));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const q = query.trim().toLowerCase();
  const filtradas = todos.filter(e => {
    if (zonaSel && e.zona_id !== zonaSel) return false;
    if (!q) return true;
    return (
      (e.nombre || "").toLowerCase().includes(q) ||
      (e.id_oficial || "").toLowerCase().includes(q) ||
      (e.colonia || "").toLowerCase().includes(q)
    );
  });

  return (
    <section className="card-base p-0 overflow-hidden" data-testid="seccion-solicitar-pipa">
      <div className="p-3 border-b border-slate-200">
        <div className="text-sm font-bold uppercase tracking-wide text-slate-700">
          <i className="fa-solid fa-truck-droplet mr-1.5"/>Solicitar pipa por parque
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          Lista completa · todas las zonas · {todos.length} parques
        </div>
      </div>

      <div className="p-3 flex flex-wrap items-center gap-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold uppercase text-slate-500">Zona</label>
          <select
            value={zonaSel}
            onChange={(e) => setZonaSel(e.target.value)}
            data-testid="lista-filtro-zona"
            className="border border-slate-300 rounded-md px-2 py-1.5 text-sm"
            style={{ minWidth: 110 }}
          >
            <option value="">Todas</option>
            {ZONAS.map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        </div>
        <input
          type="text"
          placeholder="Buscar por nombre, ID o colonia…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="lista-buscar"
          className="flex-1 min-w-[200px] border border-slate-300 rounded-md px-3 py-1.5 text-sm"
        />
        {(zonaSel || query) && (
          <button
            type="button"
            onClick={() => { setZonaSel(""); setQuery(""); }}
            data-testid="lista-limpiar"
            className="text-xs text-blue-700 hover:underline"
          >
            <i className="fa-solid fa-xmark mr-1"/>Limpiar
          </button>
        )}
        <span className="ml-auto text-xs text-slate-600">
          {loading ? <><i className="fa-solid fa-spinner fa-spin mr-1"/>Cargando…</> : <>Mostrando <strong>{filtradas.length}</strong></>}
        </span>
      </div>

      <div className="overflow-x-auto" style={{ maxHeight: 600, overflowY: "auto" }}>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">
            <i className="fa-solid fa-spinner fa-spin text-2xl mb-2 block"/>
            Cargando parques de todas las zonas…
          </div>
        ) : filtradas.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            <i className="fa-solid fa-inbox text-3xl mb-2 block text-slate-300"/>
            Sin resultados con esos filtros.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                <th className="p-3">ID</th>
                <th className="p-3">Nombre</th>
                <th className="p-3">Zona</th>
                <th className="p-3">Colonia</th>
                <th className="p-3">Estado riego</th>
                <th className="p-3 text-center">Solicitar</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map(e => (
                <FilaSolicitar key={e.id} e={e} onSolicitar={() => onSeleccionar(e)} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function FilaSolicitar({ e, onSolicitar }) {
  return (
    <tr
      className="border-t border-slate-100 hover:bg-slate-50"
      data-testid={`fila-parque-${e.id_oficial}`}
    >
      <td className="p-3 mono text-xs whitespace-nowrap">{e.id_oficial}</td>
      <td className="p-3 font-medium">
        {e.nombre}
        {e.prioridad && (
          <span
            className="pill-tiny ml-2"
            style={{
              background: e.prioridad === "ALTA" ? "#FEE2E2" : e.prioridad === "MEDIA" ? "#FEF3C7" : "#DCFCE7",
              color:      e.prioridad === "ALTA" ? "#B91C1C" : e.prioridad === "MEDIA" ? "#B45309" : "#15803D",
              fontSize: "0.65rem", fontWeight: 700,
            }}
            data-testid={`pill-prioridad-${e.id_oficial}`}
          >{e.prioridad}</span>
        )}
        {e.estado_sistema_riego === "FUERA_DE_SERVICIO" && (
          <span
            className="pill-tiny ml-1"
            style={{ background: "#FEF3C7", color: "#92400E", fontSize: "0.65rem", fontWeight: 700 }}
            title="Sistema fuera de servicio"
          ><i className="fa-solid fa-triangle-exclamation mr-1"/>FUERA SERV.</span>
        )}
      </td>
      <td className="p-3"><span className="pill-tiny pill-zone">{e.zona_id}</span></td>
      <td className="p-3 text-slate-500 text-xs">{e.colonia || "—"}</td>
      <td className="p-3"><PillEstado valor={e.estado_riego}/></td>
      <td className="p-3 text-center">
        <button
          type="button"
          onClick={onSolicitar}
          data-testid={`btn-solicitar-fila-${e.id_oficial}`}
          title="Solicitar pipa para este parque"
          aria-label={`Solicitar pipa para ${e.nombre}`}
          className="row-solicitar-btn"
          style={{
            background: "#0EA5E9", color: "white", border: "none",
            padding: "0.4rem 0.8rem", borderRadius: 8, fontSize: "0.85rem",
            fontWeight: 700, cursor: "pointer",
          }}
        >
          <i className="fa-solid fa-truck-droplet"/>
        </button>
      </td>
    </tr>
  );
}

/* ── Modal: buscar parque para solicitar pipa ─────────────── */
function BuscarParqueModal({ onClose, onSeleccionar }) {
  const [query, setQuery] = useState("");
  const [zonaSel, setZonaSel] = useState("");
  const [espacios, setEspacios] = useState([]);
  const [loading, setLoading] = useState(false);

  /* Búsqueda en servidor con debounce.  El endpoint /api/espacios soporta
     ?q= (regex en nombre/id_oficial/colonia) y ?zona_id=. Esto evita el cap
     de 500 docs y trabaja con bases grandes (>1k parques en producción). */
  useEffect(() => {
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { limit: 200 };
        if (query.trim()) params.q = query.trim();
        if (zonaSel) params.zona_id = zonaSel;
        const r = await axios.get(`${API}/espacios`, { params, signal: ctl.signal });
        setEspacios(r.data || []);
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
          toast.error("Error cargando parques: " + (e.response?.data?.detail || e.message));
        }
      } finally {
        setLoading(false);
      }
    }, 250);  // debounce 250ms
    return () => { clearTimeout(t); ctl.abort(); };
  }, [query, zonaSel]);

  const filtrados = espacios.slice(0, 100);

  return (
    <div className="mp-bg" onClick={onClose} data-testid="modal-buscar-parque">
      <div className="mp-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <header className="mp-head">
          <div>
            <div className="mp-title"><i className="fa-solid fa-magnifying-glass mr-2"/>Buscar parque</div>
            <div className="mp-sub">Selecciona el parque al que quieres solicitar pipa</div>
          </div>
          <button className="mp-close" onClick={onClose} data-testid="btn-cerrar-buscar"><i className="fa-solid fa-xmark"/></button>
        </header>

        <div className="p-3 space-y-2 border-b border-slate-200">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Nombre, ID o colonia…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="input-buscar-parque"
              autoFocus
              className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
            <select
              value={zonaSel}
              onChange={(e) => setZonaSel(e.target.value)}
              data-testid="select-zona-buscar"
              className="border border-slate-300 rounded-md px-2 py-2 text-sm"
            >
              <option value="">Todas zonas</option>
              {ZONAS.map(z => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
        </div>

        <div className="overflow-y-auto" style={{ maxHeight: 420 }}>
          {loading ? (
            <div className="p-6 text-center text-sm text-slate-500"><i className="fa-solid fa-spinner fa-spin mr-1"/>Cargando…</div>
          ) : filtrados.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">Sin resultados.</div>
          ) : (
            <ul>
              {filtrados.map(e => (
                <li
                  key={e.id}
                  onClick={() => onSeleccionar(e)}
                  data-testid={`opcion-parque-${e.id_oficial}`}
                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-slate-100 flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium text-sm">{e.nombre}</div>
                    <div className="text-xs text-slate-500">
                      <span className="mono">{e.id_oficial}</span> · {e.colonia || "—"}
                    </div>
                  </div>
                  <span className="pill-tiny pill-zone">{e.zona_id}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
